import { useMemo, useState } from "react";

import clsx from "clsx";
import { groupBy, sortBy } from "lodash";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { FilterButtonSelect } from "@features/metrics-explorer/components/filter-button-select";
import { QuickFilterButton } from "@features/metrics-explorer/components/quick-filter-button";
import { useQuickFilterOptions } from "@features/metrics-explorer/hooks";
import { FilterButtonActive } from "@features/sales-v3/top-level-filters/components/filter-button-active";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from "@shared/buttons";
import { Modal } from "@shared/modal";
import { Select } from "@shared/selects";

import { type AdoptedMetrics, type MetricFilter } from "../types";

interface Props {
  isOpen: boolean;
  isDeletable?: boolean;
  hideFilters?: boolean;
  showFtRtnMetrics?: boolean;
  adoptedMetrics: AdoptedMetrics[];
  adoptableMetrics: SalesPageMetric[];
  onClose(): void;
  onSubmit(value: { metricId: string; filters: MetricFilter[] }): void;
  onDelete?(): void;
  initialMetricId?: string;
  initialFilters?: MetricFilter[];
}

export const MetricModal = (props: Props) => {
  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      titleContent={<div className="font-bold text-xl">Add Benchmark</div>}
    >
      {props.isOpen ? <Content {...props} /> : <div className="h-[20vh]"></div>}
    </Modal>
  );
};

const Content = ({
  isDeletable,
  hideFilters,
  showFtRtnMetrics,
  adoptableMetrics,
  adoptedMetrics,
  onClose,
  onSubmit,
  onDelete,
  initialMetricId,
  initialFilters,
}: Props) => {
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();

  const [metricId, setMetricId] = useState<string | null>(
    initialMetricId ?? null,
  );
  const [filters, setFilters] = useState<MetricFilter[]>(initialFilters ?? []);

  const [aboutToDelete, setAboutToDelete] = useState(false);

  const metric = useMemo(
    () => adoptableMetrics.find(({ id }) => id === metricId),
    [adoptableMetrics, metricId],
  );

  const topLevelFilters = useMemo(() => {
    const grouped = groupBy(filters, "breakdown");
    return Object.entries(grouped).map(([breakdown, values]) => ({
      key: breakdown,
      values: values.map((e) => e.value),
    }));
  }, [filters]);

  const quickFilterOptions = useQuickFilterOptions();

  const visibleQuickFilterOptions = quickFilterOptions.filter(
    (e) =>
      !filters.find(
        (filter) =>
          filter.breakdown === e.breakdown && filter.value === e.value,
      ),
  );

  const adoptedMetricIds = adoptedMetrics.map((e) => e.metricId);

  const metricOptions = useMemo(() => {
    return adoptableMetrics
      .filter(
        (m) =>
          showFtRtnMetrics ||
          (!showFtRtnMetrics && !m.id.endsWith("Ft") && !m.id.endsWith("Rtn")),
      )
      .filter(
        (m) => !adoptedMetricIds.includes(m.id) || m.id === initialMetricId,
      )
      .map((m) => ({
        label:
          m.dashboardV2Name ||
          m.metadata.overviewPageMetricName ||
          m.metadata.name ||
          m.name,
        value: m,
      }));
  }, [adoptableMetrics, adoptedMetricIds, initialMetricId]);

  return (
    <div className="mx-0.5 -mt-1 space-y-4">
      {!hideFilters && (
        <div>
          <label className="mb-1.5 font-bold text-[11px]">
            FILTERS (OPTIONAL)
          </label>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {visibleQuickFilterOptions.length > 0 && (
                <div className="flex items-center gap-2">
                  {visibleQuickFilterOptions.map((e) => (
                    <QuickFilterButton
                      key={e.value}
                      filter={e}
                      onClick={() => {
                        const newFilters = [...filters, { ...e }];
                        setFilters(newFilters);
                      }}
                    />
                  ))}
                </div>
              )}
              <FilterButtonSelect
                breakdownConfigs={breakdownConfigs}
                breakdowns={topLevelFilters}
                onChange={(values) => {
                  const newFilters = values.flatMap((breakdown) =>
                    breakdown.values.map((value) => ({
                      breakdown: breakdown.key,
                      value,
                    })),
                  );

                  setFilters(newFilters);
                }}
              />
            </div>
            {topLevelFilters.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {topLevelFilters.map((e) => (
                  <FilterButtonActive
                    key={e.key}
                    breakdown={e}
                    tooltipPlacement="left"
                    onDelete={() => {
                      const newFilters = filters.filter(
                        (v) => v.breakdown !== e.key,
                      );

                      setFilters(newFilters);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="space-y-3">
        <div>
          <Select
            label={<span className="font-bold text-[11px]">METRIC</span>}
            className={clsx(
              "focus:outline-none focus:ring-cosmic focus:border-cosmic focus:ring-1",
              "focus:ring-inset focus-within:outline-none focus-within:ring-inset",
            )}
            menuClassName="z-[9998] max-h-[20rem]"
            onChange={(metric) => {
              setMetricId(metric ? metric.id : null);
            }}
            isSearchable
            value={metric}
            options={sortBy(metricOptions, "label")}
          />
        </div>
      </div>
      <div className="border-b border-gray-1"></div>
      {aboutToDelete ? (
        <div
          className={clsx(
            "bg-mars-2 border border-mars-1 rounded-lg py-[9px] px-4",
            "flex items-center justify-between mt-[-18px]",
          )}
        >
          <h3 className="text-sm font-bold mb-0 relative">Delete benchmark?</h3>
          <div className="flex items-center gap-2">
            <PrimaryButton
              className="font-bold text-xs bg-mars"
              onClick={() => {
                onDelete?.();
              }}
            >
              YES, DELETE
            </PrimaryButton>
            <SecondaryButton
              className="font-bold text-xs"
              onClick={() => setAboutToDelete(false)}
            >
              NO, CANCEL
            </SecondaryButton>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isDeletable && initialMetricId && (
              <TertiaryButton
                className="font-bold flex items-center gap-2"
                onClick={() => setAboutToDelete(true)}
              >
                <i className={clsx("fa-regular fa-trash")} />
                DELETE
              </TertiaryButton>
            )}
          </div>
          <div className="flex items-center justify-end gap-2">
            <TertiaryButton className="font-bold" onClick={() => onClose()}>
              CANCEL
            </TertiaryButton>
            <PrimaryButton
              className="font-bold flex items-center gap-2"
              disabled={!metricId}
              onClick={() => {
                if (metricId) {
                  onSubmit({
                    metricId,
                    filters,
                  });
                }
              }}
            >
              {initialMetricId ? "UPDATE" : "ADD BENCHMARK"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );
};
